import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { PageHistoryActions, PageHistoryNav, TitlebarContextProps } from './TitlebarContext.types'

const NO_PAGE_HISTORY: PageHistoryNav = { canGoBack: false, canGoForward: false, backList: [], forwardList: [] }

const TitlebarContext = createContext<TitlebarContextProps | undefined>(undefined)

export const TitlebarContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null)
  const [menusVisible, setMenusVisible] = useState(false)
  const closeActiveMenu = useCallback(() => setActiveMenuIndex(null), [])

  // Sidebar toggle callbacks (registered by MainLayout, called by Titlebar)
  const sidebarToggleRef = useRef<(() => void) | null>(null)
  const rightSidebarToggleRef = useRef<(() => void) | null>(null)

  // Hover-peek handlers (registered by MainLayout, called by the Titlebar icon)
  const sidebarPeekEnterRef = useRef<(() => void) | null>(null)
  const sidebarPeekLeaveRef = useRef<(() => void) | null>(null)

  // Exit-to-full callback (registered by MainLayout, called by WindowControls on Windows)
  const exitToFullRef = useRef<(() => void) | null>(null)

  // Sidebar open state (set by MainLayout, read by Titlebar for icon switching)
  const [sidebarLeftOpen, setSidebarLeftOpen] = useState(true)
  const [sidebarRightOpen, setSidebarRightOpen] = useState(true)
  const [rightSidebarAvailable, setRightSidebarAvailable] = useState(false)

  // Tracks whether MainLayout is mounted (used by Titlebar to show app controls on web)
  const [mainLayoutActive, setMainLayoutActive] = useState(false)

  // TabBar portal slot: Titlebar provides a DOM element, MainLayout portals TabBar into it
  const tabBarSlotRef = useRef<HTMLDivElement | null>(null)
  const [tabBarSlotReady, setTabBarSlotReady] = useState(false)

  // Stable callback ref for Titlebar to register the slot element
  const registerTabBarSlot = useCallback((el: HTMLDivElement | null) => {
    tabBarSlotRef.current = el
    setTabBarSlotReady(!!el)
  }, [])

  // Union shape portal slot: WindowContext provides a DOM element, MainLayout portals ContentUnionShape into it
  const unionShapeSlotRef = useRef<HTMLDivElement | null>(null)
  const [unionShapeSlotReady, setUnionShapeSlotReady] = useState(false)

  const registerUnionShapeSlot = useCallback((el: HTMLDivElement | null) => {
    unionShapeSlotRef.current = el
    setUnionShapeSlotReady(!!el)
  }, [])

  // Bump portal slot: inside Titlebar so bump layers share the same stacking context as tab labels.
  // This prevents Chromium's compositor from reordering bump above labels after content repaints.
  const bumpSlotRef = useRef<HTMLDivElement | null>(null)
  const [bumpSlotReady, setBumpSlotReady] = useState(false)

  const registerBumpSlot = useCallback((el: HTMLDivElement | null) => {
    bumpSlotRef.current = el
    setBumpSlotReady(!!el)
  }, [])

  // Seitenverlauf (#225): Sprünge von MainLayout, Zustand für die Pfeile der Titlebar.
  const pageHistoryRef = useRef<PageHistoryActions | null>(null)
  const [pageHistory, setPageHistory] = useState<PageHistoryNav>(NO_PAGE_HISTORY)

  const contextValue = useMemo<TitlebarContextProps>(() => ({
    activeMenuIndex, setActiveMenuIndex, menusVisible, setMenusVisible, closeActiveMenu,
    sidebarToggleRef,
    rightSidebarToggleRef,
    sidebarPeekEnterRef,
    sidebarPeekLeaveRef,
    exitToFullRef,
    sidebarLeftOpen,
    setSidebarLeftOpen,
    sidebarRightOpen,
    setSidebarRightOpen,
    rightSidebarAvailable,
    setRightSidebarAvailable,
    mainLayoutActive,
    setMainLayoutActive,
    tabBarSlotRef,
    tabBarSlotReady,
    registerTabBarSlot,
    unionShapeSlotRef,
    unionShapeSlotReady,
    registerUnionShapeSlot,
    bumpSlotRef,
    bumpSlotReady,
    registerBumpSlot,
    pageHistoryRef,
    pageHistory,
    setPageHistory,
  }), [activeMenuIndex, menusVisible, sidebarLeftOpen, sidebarRightOpen, rightSidebarAvailable, mainLayoutActive, tabBarSlotReady, unionShapeSlotReady, bumpSlotReady, closeActiveMenu, registerTabBarSlot, registerUnionShapeSlot, registerBumpSlot, pageHistory])

  return (
    <TitlebarContext.Provider value={contextValue}>
      {children}
    </TitlebarContext.Provider>
  )
}

export const useTitlebarContext = (): TitlebarContextProps => {
  const context = useContext(TitlebarContext)
  if (context === undefined) {
    throw new Error('useTitlebarContext must be used within a TitlebarContext')
  }
  return context
}
